/**
 * UTM Source Category Mapping
 *
 * Maps UTM parameters to business categories for analytics and reporting.
 *
 * Canonical campaign tagging for the Maison Mytheresa Yacht launch:
 *
 *   Performance Marketing
 *     Meta:        utm_source=paid-social-upperfunnel
 *                  utm_medium=facebook
 *                  utm_campaign=us_bm_MaisonMytheresaSS26
 *
 *   EMM
 *     Newsletters: utm_source=EML
 *                  utm_medium=sign_ups
 *                  utm_campaign=maisonmytheresa
 *
 *   Onsite
 *     TLB:         utm_source=onsite&utm_medium=tlb&utm_campaign=ww_maisonmytheresa
 *     Overlay:     utm_source=onsite&utm_medium=overlay&utm_campaign=ww_maisonmytheresa
 *     ContentCard: utm_source=onsite&utm_medium=contentcard&utm_campaign=ww_maisonmytheresa
 *
 *   App
 *     PushNotif:   utm_source=app_notification&utm_medium=push&utm_campaign=ww_maisonmytheresa
 *     InAppMsg:    utm_source=app_inappmessage&utm_medium=inapp&utm_campaign=ww_maisonmytheresa
 *
 * The campaign suffix matters per channel — Onsite/App use `ww_maisonmytheresa`,
 * EMM uses `maisonmytheresa`, Meta uses `us_bm_MaisonMytheresaSS26`. We match
 * on `campaign.includes('maisonmytheresa')` so all three forms classify
 * correctly without a per-campaign whitelist.
 */

/**
 * Maps UTM parameters to a business category
 * @param {Object} params - UTM parameters { source, medium, campaign }
 * @param {string} method - How the referral was detected ('utm_params', 'referrer', 'unknown')
 * @returns {string} Category name
 */
export function mapUTMToCategory(params, method) {
  const source = (params.source || '').toLowerCase();
  const medium = (params.medium || '').toLowerCase();
  const campaign = (params.campaign || '').toLowerCase();
  const campaignIsMaison = campaign.includes('maisonmytheresa');

  // APP Placements (3-param match - most specific first)
  if (source === 'app_notification' && medium === 'push' && campaignIsMaison) {
    return 'APP Placements - Push';
  }
  if (source === 'app_inappmessage' && medium === 'inapp' && campaignIsMaison) {
    return 'APP Placements - In-App';
  }

  // Onsite Placements (3-param match)
  if (source === 'onsite' && campaignIsMaison) {
    if (medium === 'tlb') return 'Onsite Placements - TLB';
    if (medium === 'overlay') return 'Onsite Placements - Overlay';
    if (medium === 'contentcard') return 'Onsite Placements - Content Card';
  }

  // Paid Social — Meta is the canonical performance source. Also catch any
  // facebook-medium fallback that targets a Maison Mytheresa campaign.
  if (source === 'paid-social-upperfunnel') {
    return 'Paid Social';
  }
  if (medium === 'facebook' && (campaign.includes('roe_bm') || campaignIsMaison)) {
    return 'Paid Social';
  }

  // SEA (Search Engine Advertising)
  if (source === 'sea_nb' || source === 'sea_pla') {
    return 'SEA';
  }
  if (medium === 'google' && campaign.includes('sea')) {
    return 'SEA';
  }

  // Organic Social
  if (source === 'organic_social_media') {
    return 'Organic Social';
  }

  // Newsletter — canonical: source=EML, medium=sign_ups. Keep the looser
  // `eml*` source match to cover legacy newsletter links.
  if (source === 'eml' || source.startsWith('eml')) {
    return 'Newsletter';
  }

  // Fallback based on method
  if (!source || source === 'direct') {
    return method === 'referrer' ? 'Referral' : 'Direct';
  }

  return 'Unknown';
}

/**
 * All available category names
 */
export const ALL_CATEGORIES = [
  'Paid Social',
  'SEA',
  'Organic Social',
  'APP Placements - Push',
  'APP Placements - In-App',
  'Onsite Placements - TLB',
  'Onsite Placements - Overlay',
  'Onsite Placements - Content Card',
  'Newsletter',
  'Direct',
  'Referral',
  'Unknown',
];
